/**
 * The module editor before its module lands: the header's lines, the real
 * section tabs, and a form in bones.
 *
 * @module components/studio/ModuleEditorSkeleton
 */

import React from "react";

import { MODULE_TABS } from "@/components/studio/module-editor-model";
import { LinkTabs, Panel } from "@/components/ui";
import { Bone, SkeletonField, SkeletonText } from "@/components/ui/SkeletonParts";

export function ModuleEditorSkeleton({
  tabBase,
  label = undefined,
  "data-testid": testId,
}: {
  /** The module's address, which its tabs extend. */
  tabBase: string;
  /** Names the wait for a screen reader; absent when an outer region already does. */
  label?: string;
  "data-testid"?: string;
}): React.ReactElement {
  return (
    <section
      role={label ? "status" : undefined}
      aria-live={label ? "polite" : undefined}
      className="min-w-0 space-y-4"
      data-testid={testId}
    >
      {label ? <span className="sr-only">{label}</span> : null}
      <div aria-hidden="true" className="flex flex-col gap-3">
        <Bone className="h-6 w-2/3" />
        <div className="flex flex-wrap gap-2">
          {["w-32", "w-20", "w-28", "w-16"].map((width) => (
            <Bone key={width} className={`h-5 ${width}`} />
          ))}
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Bone className="h-4 w-56" />
          <Bone className="h-8 w-36" />
        </div>
      </div>
      <LinkTabs
        aria-label="Module sections"
        items={MODULE_TABS.map((tab) => ({
          to: `${tabBase}/${tab.id}`,
          label: tab.label,
          icon: tab.icon,
        }))}
      />
      <Panel>
        <div aria-hidden="true" className="flex flex-col gap-5">
          <SkeletonText lines={1} />
          <SkeletonField />
          <div className="grid gap-4 sm:grid-cols-2">
            <SkeletonField />
            <SkeletonField />
          </div>
          <Bone className="h-24 w-full" />
        </div>
      </Panel>
    </section>
  );
}
